/**
 * Every move a person can make on a task, drawn from the state machine rather than
 * from a second list of states.
 *
 * `taskMachine.eventsFrom(status)` is the source (`packages/shared/src/states.ts`),
 * so a transition added there appears here the moment it has a label — and a page
 * that offered "Start" on a blocked task would be a second state check, which
 * `.docs/00-index.md` §5 bans in as many words.
 *
 * Two events are deliberately not buttons. `lease_expired` is the sweep's
 * (`tasks.releaseStaleLeases`), not a person's; and `approve_review` is
 * `tasks_complete` under a different label, because there is no
 * `tasks_approve_review` tool — completing a task in `review` **is** the approval,
 * and it is staff-only (`.docs/reports/07-report.md`).
 *
 * Placement (#28–#29): the one next move by state is primary beside Status; other
 * status moves stay secondary there; Assign / Comment / Log time / Cancel live
 * beside their own facts or sections, not in a top-right dump.
 *
 * Ownership (#37): when a staff person is looking at a task somebody else holds,
 * the work moves (claim / start / complete / block / …) stay hidden — Comment, Log
 * time, Assign and Cancel remain, so a manager can still re-route or kill it.
 * Unassigned, or assigned to the reader, keeps every move the machine allows.
 */

import { taskMachine, type TaskEvent, type TaskView } from '@fareast/shared'

import { messageOf } from '../../../api/problem.js'
import { reviewOf, useStudioAction, type StudioActionName } from '../../../api/studio.js'
import { usePrincipal } from '../../../auth/principal.js'
import { Button, ButtonRow, type ButtonVariant } from '../../../components/portal/Button.js'
import { Stack } from '../../../components/portal/Layout.js'
import { Notice } from '../../../components/portal/Status.js'
import { Muted, Small } from '../../../components/portal/Text.js'
import { ActionDialog } from '../../../components/studio/ActionDialog.js'
import { AssignDialog } from '../../../components/studio/AssignDialog.js'
import { useOwnMoveOnItsWay } from '../../../components/studio/CellEdit.js'
import { NeedsReviewNotice } from '../../../components/studio/NeedsReviewNotice.js'
import { TaskMoreMenu } from '../../../components/studio/TaskMoreMenu.js'
import {
  CLOSING_WORDS,
  DIALOG_MOVES,
  canMutateWorkOf,
  moveLabel,
  moveOf,
  moveWindowOf,
  overtaken,
} from '../../../components/studio/taskWords.js'

/* Re-exported so the callers that had it from here keep one import path. */
export { canMutateWorkOf }
import { BlockControl } from './BlockControl.js'
import { KEY_TARGET } from './keyTargets.js'

type Move = {
  readonly action: StudioActionName
  readonly label: string
  /** The dialog's own heading, when the button's words are not a title. */
  readonly title: string
}

/**
 * Work moves hidden when somebody else holds the task. Cancel is not among them —
 * and neither is `approve_review`: `submit_for_review` keeps the assignee, so a task
 * in review is by construction held by whoever did the work, and approving it is
 * precisely somebody else's move.
 */
const WORK_MUTATIONS: ReadonlySet<TaskEvent> = new Set([
  'claim',
  'start',
  'complete',
  'block',
  'unblock',
  'submit_for_review',
  'release',
])

/**
 * The one next action by state (#29): Claim → Start → Send for review → Complete
 * (and Approve / Unblock when those are the only way forward).
 */
const PRIMARY_ORDER: readonly TaskEvent[] = [
  'claim',
  'start',
  'submit_for_review',
  'complete',
  'approve_review',
  'unblock',
]

export function primaryEventOf(events: readonly TaskEvent[]): TaskEvent | null {
  for (const event of PRIMARY_ORDER) {
    if (events.includes(event)) return event
  }
  return null
}

export type LifecycleProps = { readonly task: TaskView }

/**
 * Primary + secondary status moves — Claim / Start / Complete / Block / … —
 * meant to sit beside the Status fact, not in the topbar.
 */
export function StatusMoves({ task }: LifecycleProps) {
  const claim = useStudioAction('tasks_claim')
  const start = useStudioAction('tasks_start')
  /*
   * Either of the page's own two moves on its way: the windows beside them wait at
   * their buttons. A move's window is made from what it was opened over and from
   * nothing else (`ActionForm` `useOpenedFrom`), so one opened in the half-second
   * before the page has been read again can only say it is out of date and be opened
   * a second time. `ActionDialog` gives the focus back to a button that waited with it.
   */
  const moving = claim.isPending || start.isPending
  const principal = usePrincipal()
  const userId = principal.kind === 'staff_user' ? principal.userId : null

  const events = taskMachine.eventsFrom(task.status)
  const finished = taskMachine.isTerminal(task.status)
  const canMutateWork = canMutateWorkOf(task, userId)
  const primary = primaryEventOf(
    canMutateWork ? events : events.filter((event) => !WORK_MUTATIONS.has(event)),
  )

  if (finished) return null

  // Cancel is under More and Block has its own control above: neither is drawn twice.
  const dialogEvents = events.filter(
    (event): event is TaskEvent =>
      event !== 'cancel' && event !== 'block' && DIALOG_MOVES[event] !== undefined,
  )

  return (
    <Stack gap="tight">
      {canMutateWork || task.status === 'review' ? null : (
        <Small>
          <Muted>
            {task.assignee_name === null
              ? `${takenWord(task)} somebody else — Claim, Start and Complete stay with them.`
              : `${takenWord(task)} ${task.assignee_name} — Claim, Start and Complete stay with them.`}
          </Muted>
        </Small>
      )}
      <ButtonRow>
        {canMutateWork && events.includes('claim') ? (
          <Button
            variant={primary === 'claim' ? 'primary' : undefined}
            disabled={claim.isPending}
            onClick={() => {
              claim.mutate({ task_ref: task.ref })
            }}
          >
            {claim.isPending ? 'Working…' : moveLabel('claim')}
          </Button>
        ) : null}

        {canMutateWork && events.includes('start') ? (
          <Button
            id={KEY_TARGET.start}
            variant={primary === 'start' ? 'primary' : undefined}
            disabled={start.isPending}
            onClick={() => {
              start.mutate(moveOf(task))
            }}
          >
            {start.isPending ? 'Working…' : moveLabel('start')}
          </Button>
        ) : null}

        {canMutateWork && events.includes('block') ? (
          <BlockControl task={task} disabled={moving} />
        ) : null}

        {dialogEvents.map((event) => {
          const move = DIALOG_MOVES[event]
          if (move === undefined) return null
          if (!canMutateWork && WORK_MUTATIONS.has(event)) return null
          return (
            <DialogMove
              key={event}
              move={move}
              task={task}
              variant={event === primary ? 'primary' : undefined}
              disabled={moving}
            />
          )
        })}
      </ButtonRow>

      <Outcome task={task} result={claim.data} error={claim.isError ? claim.error : null} />
      <Outcome task={task} result={start.data} error={start.isError ? start.error : null} />
    </Stack>
  )
}

/** Assign — beside the Assigned fact. */
export function AssignControl({ task }: LifecycleProps) {
  if (taskMachine.isTerminal(task.status)) return null
  return (
    <AssignDialog
      task={task}
      size="sm"
      trigger={
        <Button size="sm" id={KEY_TARGET.assign}>
          Assign
        </Button>
      }
    />
  )
}

/**
 * Comment — near the Comments section. Visibility must be chosen (#33).
 *
 * `required` because the published schema defaults `visibility` to `internal` so an
 * agent written before it existed still works; a person at this form still chooses.
 */
export function CommentControl({ task }: LifecycleProps) {
  if (taskMachine.isTerminal(task.status)) return null
  return (
    <ActionDialog
      action="tasks_comment"
      label="Comment"
      title="Add a comment"
      submitLabel="Comment"
      fixed={{ task_ref: task.ref }}
      required={['visibility']}
      size="sm"
    />
  )
}

/** Log time — near the Time section. Re-export so callers keep one import path. */
export { LogTimeControl } from './LogTimeControl.js'

/**
 * The rare moves, folded — "Cancel and other rare actions under More" (#20).
 *
 * It used to be a bare "Cancel this task" beside "Approval required", which is what
 * the owner called "the crowded top-right action row". The fold is the same
 * `TaskMoreMenu` the list rows and the board cards draw, so the rare moves are one
 * set wherever a task is looked at.
 */
export function MoreControls({ task }: LifecycleProps) {
  /*
   * It waits for a move of the reader's own on this task, as the buttons beside Start
   * do (`moving`, above) and as a row's More does: its windows are made from where the
   * task stands, and one opened before the page had been read again after `s` could
   * only say it was out of date.
   */
  const moving = useOwnMoveOnItsWay(task)
  return <TaskMoreMenu task={task} surface="page" disabled={moving} />
}

/**
 * What a plain button's call came back with.
 *
 * A `needs_review` answer is a **success** that did not happen (`api/studio.ts`
 * `reviewOf`), so it is not on the error branch and says so in its own words.
 */
function Outcome({
  task,
  result,
  error,
}: LifecycleProps & { readonly result: unknown; readonly error: unknown }) {
  const review = reviewOf(result)
  if (review !== null) return <NeedsReviewNotice review={review} />
  if (error === null) return null
  return (
    <Notice need role="alert">
      {/* Somebody else's change, in a person's words; anything else, in the API's own. */}
      {overtaken(task.ref, error, 'move') ?? messageOf(error)}
    </Notice>
  )
}

/**
 * A move with fields — the reason, the outcome, the minutes.
 *
 * `fixed` is the task and what the move is made from (`taskWords.ts` `moveOf`), and
 * nothing else: `ActionForm` does not draw a field the page has already answered, so
 * the dialog asks for the reason and not for the reference of the task it was opened
 * from — and a move made from a page that is no longer true is refused at its foot.
 */
function DialogMove({
  move,
  task,
  variant,
  disabled,
}: {
  readonly move: Move
  readonly task: TaskView
  readonly variant?: ButtonVariant | undefined
  /** True while Claim or Start, beside it, is on its way (`ActionDialog` `disabled`). */
  readonly disabled: boolean
}) {
  return (
    <ActionDialog
      action={move.action}
      label={move.label}
      title={move.title}
      submitLabel={move.label}
      {...moveWindowOf(task)}
      size="sm"
      variant={variant}
      disabled={disabled}
    />
  )
}

/**
 * Which of the two closing moves is which (#25).
 *
 * Drawn under the facts strip rather than inside the Status cell, which is one of
 * seven columns: the sentence wrapped to six lines there and stretched the whole
 * block, which is the opposite of the "compact facts block" the same list asks for
 * (#19). It is asked before either button is pressed, so it stays on the page rather
 * than inside their dialogs.
 */
export function ClosingNote({ task }: LifecycleProps) {
  const events = taskMachine.eventsFrom(task.status)
  // The move that makes sense here is the one described: review when it is needed (#25).
  const words =
    task.needs_review && events.includes('submit_for_review')
      ? CLOSING_WORDS.submit_for_review
      : events.includes('complete')
        ? CLOSING_WORDS.complete
        : null
  if (words === null) return null
  return (
    <Small>
      <Muted>{words}</Muted>
    </Small>
  )
}

/**
 * "Assigned to" or "taken by" — the same two words the opening sentence uses.
 *
 * The page used to say a task was not claimed and, three inches below, that the same
 * person "holds this". Assigned means it is theirs to pick up; taken means they
 * picked it up and the clock is running, which is what `claimed` and `in_progress`
 * are.
 */
function takenWord(task: TaskView): string {
  return task.status === 'claimed' || task.status === 'in_progress'
    ? 'This has been taken by'
    : 'This is assigned to'
}
